import React from 'react';
import { Button, Card, Typography } from 'antd';
import PropTypes from 'prop-types';

import ControlledLexicalEditor from './ControlledLexicalEditor';
import { getTemplate } from './EditorNavigation';

const { Title } = Typography;

const formatCamelCase = (camelStr) => {
    if (!camelStr) return '';

    // Insert spaces before capital letters and capitalize first letter
    return camelStr
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
};

const formatSectionTitle = (section) => {
    if (!section) return '';

    const [mainSection, subSection] = section.split('.');
    if (!mainSection || !subSection) return section;

    const mainFormatted = mainSection.charAt(0).toUpperCase() + mainSection.slice(1);
    const subFormatted = formatCamelCase(subSection);
    return `${mainFormatted} - ${subFormatted}`;
};

/**
 * SectionEditor - Editor for strategy-specific breakdown sections
 *
 * This is a presentational component that receives content and onChange
 * from the parent. All state management is handled by useStrategyEditor.
 */
export default function SectionEditor({
    section,
    content,
    contentVersion,
    onChange,
    onApplyTemplate,
    isSaving,
    isReadOnly = false
}) {
    const template = getTemplate(section);
    const title = formatSectionTitle(section);
    const hasContent = content && content.length > 0;

    const handleApplyTemplate = () => {
        onApplyTemplate(template);
    };

    return (
        <div className="section-editor">
            <Card
                title={<Title level={3}>{title}</Title>}
                extra={isSaving ? <span className="saving-indicator">Saving...</span> : null}
            >
                {!hasContent && template && (
                    <Button
                        type="primary"
                        onClick={handleApplyTemplate}
                        disabled={isReadOnly}
                        style={{ marginBottom: '16px' }}
                    >
                        + TradeBook Template
                    </Button>
                )}
                <ControlledLexicalEditor
                    content={content}
                    contentVersion={contentVersion}
                    onChange={onChange}
                    isReadOnly={isReadOnly}
                    placeholder="Enter your notes here..."
                    minHeight="300px"
                />
            </Card>
        </div>
    );
}

SectionEditor.propTypes = {
    section: PropTypes.string.isRequired,
    content: PropTypes.string,
    contentVersion: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    onApplyTemplate: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
    isReadOnly: PropTypes.bool
};
